import React from 'react';

import api       from '../../api';
import utils     from '../../utils';
import values    from '../../values';
import Cookies   from 'js-cookie';
import Context   from '../../context';
import constants from '../../values/constants';

import TopMenu          from '../TopMenu';
import UserInfo         from '../UserInfo';
import CustomIcon       from '../CustomIcon';
import WalletIcon       from '../WalletIcon';
import ResponsiveView   from '../ResponsiveView';
import LanguageSwitcher from '../LanguageSwitcher';
import AcademicYearSelect from '../AcademicYearSelect';

import { Container }                     from 'react-bootstrap';
import { useTranslation }                from 'react-i18next';
import {PrimaryUserInfoAndWallet, SecondaryWallet} from '../index';
import { useQuery, UseQueryResult }      from 'react-query';
import { ISubMenuItems, ITopMenuItem }   from '../../types/common.interface';
import { IAcademicYear }                 from '../../types/academicYear.interface';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import './index.scss';

interface Props {
  children    : React.ReactNode;
  topMenuItems: ISubMenuItems[];
}

const Layout = (props: Props) => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const navigate: NavigateFunction = useNavigate();
  const getCurrentMenu = () => window.location.pathname.split("/")[3];
  const [selectedMenu, setSelectedMenu] = React.useState<ITopMenuItem>(
    getSelectedMenu()
  );
  const userType: string = utils.stringHexConversion.reverse(
    Cookies.get(constants.USER.TYPE)
  );
  const { t } = useTranslation();

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  function getSelectedMenu(): ITopMenuItem {
    const currentMenu: string = getCurrentMenu();
    return (
      props.topMenuItems.find(
        (menu: any): boolean => menu.pathname.split("/")[3] === currentMenu
      ) || props.topMenuItems[0]
    );
  }

  function onMenuClick(menu: any): void {
    navigate(menu.pathname);
    setSelectedMenu(menu);
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------------ Start Call API -------------------------------------------------

  const result: UseQueryResult<any, unknown> = useQuery([values.useQueryKeys.userInfo, userType], () =>
    api.get.common.getUserInfo()
  );

  let userInfo: any = result?.data?.result;

  const getPrimaryWallet: UseQueryResult<any, unknown> = useQuery([], () =>
      api.get.administrator.getPrimaryWallet()
    , { enabled: userType === 'ADMIN' && userInfo?.isPrimary === true}
  );

  const primaryWallet: any =  getPrimaryWallet?.data?.result?.wallet

  // ! ------------------------------------------- Start Academic Year -------------------------------------------------
  // ? The selected academic year (سال تحصیلی) is held here because Layout is the one shell that wraps every panel
  // ? screen, so a single provider covers all of them. The screens read it and add it to their query keys.

  const academicYearResult: UseQueryResult<any, unknown> = useQuery(
    [values.useQueryKeys.getAllAcademicYear],
    () => api.get.common.getAllAcademicYear()
  );

  const academicYears: IAcademicYear[] = academicYearResult?.data?.result?.docs ?? [];
  const currentAcademicYear: IAcademicYear | undefined = academicYearResult?.data?.result?.current ?? undefined;

  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState<IAcademicYear | undefined>(undefined);

  // ? Seed the selection once the list arrives: the year the user last picked, else the one the backend calls current,
  // ? else the newest. Guarded on `selectedAcademicYear` so a refetch never yanks the user back off their choice.
  React.useEffect(() => {
    if (selectedAcademicYear || academicYears.length === 0) return;

    const savedId: string | undefined = Cookies.get(constants.SELECTED_ACADEMIC_YEAR);

    setSelectedAcademicYear(
      academicYears.find((academicYear: IAcademicYear) => academicYear._id === savedId) ??
      currentAcademicYear ??
      academicYears[academicYears.length - 1]
    );
  }, [academicYears, currentAcademicYear, selectedAcademicYear]);

  function onAcademicYearChange(academicYear: IAcademicYear): void {
    Cookies.set(constants.SELECTED_ACADEMIC_YEAR, academicYear._id);
    setSelectedAcademicYear(academicYear);
  }

  // ! -------------------------------------------- End Academic Year --------------------------------------------------

  // ! ------------------------------------------------- End Call API --------------------------------------------------
  // ! =================================================================================================================

  switch (userType) {
    case 'ADMIN'             : userInfo = {...userInfo, info: { name: t(values.constants.ADMIN_NAME)                                       }}; break;
    case 'COMPANY'           : userInfo = {...userInfo, info: { name: userInfo?.companyInformation?.companyName                            }}; break;
    case 'MUNICIPALITY'      : userInfo = {...userInfo, info: { name: `${userInfo?.firstName} ${userInfo?.lastName} (${t("municipality")})`}}; break;
    case 'GOVERNMENTSHIP'    : userInfo = {...userInfo, info: { name: userInfo?.governmentShipInformation?.name                            }}; break;
    case 'SUPER_AGENT_SCHOOL': userInfo = {...userInfo, info: { name: `${userInfo?.firstName} ${userInfo?.lastName} (${t("superAgent")})`  }}; break;
    case 'PROVINCE_EDUCATION': userInfo = {...userInfo, info: { name: userInfo?.provinceEducationInformation?.name                         }}; break;
  }

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <Context.UserScreen.Provider value={{ userInfo }}>
      <Context.AcademicYear.Provider
        value={{
          years          : academicYears,
          isLoading      : academicYearResult.status === 'loading',
          selectedYear   : selectedAcademicYear,
          setSelectedYear: onAcademicYearChange,
        }}
      >
      <ResponsiveView.Container>
        <ResponsiveView.Container fluid id='layout-container'>
          <div className='header-container'>
            <div className='actions'>
              <div style={{display: 'flex', flexDirection: 'row-reverse'}}>
                <CustomIcon
                  size='large'
                  icon='fa-solid fa-right-from-bracket'
                  onClick={() => utils.cookiesValue.remove()}
                  hoverTitle={t('signOut')}
                />
                <LanguageSwitcher/>
                <AcademicYearSelect/>
              </div>
            </div>
            { userType === 'ADMIN' && userInfo?.isPrimary === true ? (
              <PrimaryUserInfoAndWallet userName={userInfo?.info?.name} wallet={primaryWallet}/>
            ) : (
              <ResponsiveView.Row className='action-wrapper'>
                <ResponsiveView.Column className='actions' style={{marginBottom: '10px'}}>
                  <UserInfo name={userInfo?.info?.name}/>
                  <WalletIcon
                    balance           = { userInfo?.wallet?.balance           }
                    availableFund     = { userInfo?.wallet?.availableFund     }
                    deptAdmin2Company = { userInfo?.wallet?.deptAdmin2Company }
                  />
                </ResponsiveView.Column>
                <ResponsiveView.Column className='actions'>
                  <SecondaryWallet
                    deptCopanies2Admin      = { userInfo?.wallet?.deptCopanies2Admin      }
                    paidTransactionCompany  = { userInfo?.wallet?.paidTransactionCompany  }
                    transactionOnlineParent = { userInfo?.wallet?.transactionOnlineParent }
                    deptCompany2Admin   = { userInfo?.wallet?.deptAdmin2Company   }
                    deptCompany2Drivers = { userInfo?.wallet?.deptCompany2Drivers }
                  />
                </ResponsiveView.Column>
              </ResponsiveView.Row>
            )}
          </div>
          <section className="screen-wrapper">
            <TopMenu onClick={onMenuClick} selected={selectedMenu} menus={props.topMenuItems}/>
            <div>{props.children}</div>
          </section>
        </ResponsiveView.Container>
      </ResponsiveView.Container>
      </Context.AcademicYear.Provider>
    </Context.UserScreen.Provider>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================
};

export default Layout;
